/**
 * The onboarding wizard's decisions (phase 7b item 3), pure and
 * table-tested (`onboarding.test.ts`): which step is done — read off the
 * tenant's own lists as well as the saved progress, so every step is
 * idempotent (a show style that exists is adopted, never made twice) and the
 * wizard works on a node, which saves no progress — which step is next, and
 * whether the wizard should open by itself.
 */
import type { OnboardingWire, OnboardingStepIdWire } from '@acp/api-contract/onboarding.js';
import { ONBOARDING_STEPS } from '@acp/api-contract/onboarding.js';

export type WizardFacts = {
  readonly showStyleIds: readonly string[];
  readonly studioIds: readonly string[];
  /** On-prem sites (the automatic cloud site not counted); null where the backend cannot say (a node). */
  readonly onPremSiteIds: readonly string[] | null;
  /** Cloud productions; null where the backend cannot say. */
  readonly cloudProductionCount: number | null;
  readonly rundownIds: readonly string[];
};

export type WizardStepStatus = 'done' | 'skipped' | 'todo';

export type WizardStep = { readonly id: OnboardingStepIdWire; readonly status: WizardStepStatus; readonly resourceId: string | null };

/** What the tenant's lists already hold for a step (its resource), or null. */
function derivedResource(id: OnboardingStepIdWire, facts: WizardFacts, saved: string | null): string | null {
  const pick = (ids: readonly string[]): string | null => (saved !== null && ids.includes(saved) ? saved : (ids[0] ?? null));
  switch (id) {
    case 'showStyle':
      return pick(facts.showStyleIds);
    case 'studio':
      return pick(facts.studioIds);
    case 'server':
      if (facts.onPremSiteIds !== null && facts.onPremSiteIds.length > 0) return pick(facts.onPremSiteIds);
      return facts.cloudProductionCount !== null && facts.cloudProductionCount > 0 ? 'cloud' : null;
    case 'rundown':
      return pick(facts.rundownIds);
  }
}

/**
 * Every step's state: done when the saved progress says so or the tenant already has what it makes; skipped when
 * saved (or, without saved progress, `localSkips`) says so; else to do.
 */
export function wizardSteps(facts: WizardFacts, progress: OnboardingWire | null, localSkips: ReadonlySet<OnboardingStepIdWire>): WizardStep[] {
  return ONBOARDING_STEPS.map((id) => {
    const saved = progress?.steps[id] ?? null;
    const resource = derivedResource(id, facts, saved?.resourceId ?? null);
    if (saved?.status === 'done') return { id, status: 'done', resourceId: saved.resourceId ?? resource };
    if (resource !== null) return { id, status: 'done', resourceId: resource };
    if (saved?.status === 'skipped' || localSkips.has(id)) return { id, status: 'skipped', resourceId: null };
    return { id, status: 'todo', resourceId: null };
  });
}

/** The first step still to do; null when every step is done or skipped. */
export function nextStep(steps: readonly WizardStep[]): OnboardingStepIdWire | null {
  return steps.find((s) => s.status === 'todo')?.id ?? null;
}

/**
 * Whether the wizard opens by itself: a tenant admin, on a backend that saves progress, who never opened it, in a
 * tenant with nothing in it yet. A node (no saved progress) never opens it unasked.
 */
export function shouldAutoOpen(progress: OnboardingWire | null, facts: WizardFacts, isTenantAdmin: boolean): boolean {
  if (progress === null || progress.status !== 'not-started' || !isTenantAdmin) return false;
  return facts.showStyleIds.length === 0 && facts.studioIds.length === 0 && facts.rundownIds.length === 0;
}

export const STEP_TITLES: Readonly<Record<OnboardingStepIdWire, string>> = {
  showStyle: 'Show style',
  studio: 'Studio',
  server: 'Server or cloud',
  rundown: 'First rundown',
};
